import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useApiClient from "@/src/hooks/useApiClient";
import React, { useRef, useState } from "react";
import useUldk from "@/src/hooks/useUldk";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import Loader from "../Loader/Loader";
import { Parcel } from "../ParcelPreview/ParcelPreview";
import { CRS_WGS84, CRS_PL1992, getProj4 } from "@/src/map/projections";

type Status = "loading" | "error" | "fulffiled" | "idle";
type UldkSearchPropsType = {
  latLng: { lat: number; lng: number };
  voivodeship: string;
  district: string;
  tenant: string;
  setParcel: ((parcel: Parcel) => void) | React.Dispatch<React.SetStateAction<Parcel | undefined>>;
};
function UldkSearch({ latLng, voivodeship, district, tenant, setParcel }: UldkSearchPropsType) {
  const {
    voivodeships,
    districts,
    fetchDistricts,
    fetchTenants,
    tenants,
    fetchPrecincts,
    precincts,
    getParcelGeometryById,
  } = useUldk({
    voivodeship,
    district,
    tenant,
  });
  const [selectedPrecinct, setSelectedPrecinct] = useState<string>();
  const itemRef = useRef<HTMLDivElement>(null);
  const { publicApiClient } = useApiClient();
  const [parcelNumber, setParcelNumber] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const { toast } = useToast();

  // Build full parcel ID: e.g. "241801_1.0001.552"
  const buildParcelId = (): string | null => {
    const precinct = selectedPrecinct || precincts[0]?.value;
    if (!precinct || !parcelNumber) return null;
    // Precinct value from ULDK is like "2418011_8.0001" (teryt.obreb)
    // or just the teryt code. We need to build: teryt.obreb.nr_dzialki
    // Full ID format: WWPPGG_T.OOOO.NR where precinct = WWPPGG_T.OOOO
    return `${precinct}.${parcelNumber}`;
  };

  const findParcel = async () => {
    const fullId = buildParcelId();
    if (!fullId) {
      toast({ variant: "destructive", title: "Wybierz obręb i wpisz numer działki" });
      return;
    }
    setStatus("loading");
    try {
      const parcelGeometry = await getParcelGeometryById(fullId);
      setParcel({ id: fullId, geometry: parcelGeometry });
      setStatus("fulffiled");
    } catch (error) {
      console.error(error);
      setStatus("error");
      toast({ variant: "destructive", title: "Nie znaleziono działki", description: `ID: ${fullId}` });
    }
  };

  const findParcelByXY = async () => {
    setStatus("loading");
    try {
      // ULDK oczekuje współrzędnych w PL-1992 (srid=2180) — konwersja z WGS84
      // przez centralnie zarejestrowany proj4.
      const proj4 = getProj4();
      const [x, y] = proj4(CRS_WGS84, CRS_PL1992, [latLng.lng, latLng.lat]);
      const { data } = await publicApiClient.get(
        `https://uldk.gugik.gov.pl/?request=GetParcelByXY&xy=${x},${y}&result=teryt&srid=2180`,
      );
      const lines = data.split("\n");
      if (lines[0]?.includes("-1") || lines.length < 2 || !lines[1]?.trim()) {
        toast({ variant: "destructive", title: "Nie znaleziono działki w tej lokalizacji" });
        setStatus("error");
        return;
      }
      const foundId = lines[1].trim();
      const parcelGeometry = await getParcelGeometryById(foundId);
      setParcel({ id: foundId, geometry: parcelGeometry });
      setStatus("fulffiled");
      toast({ variant: "success", title: "Znaleziono działkę", description: foundId });
    } catch (error) {
      console.error(error);
      setStatus("error");
      toast({ variant: "destructive", title: "Błąd wyszukiwania działki" });
    }
  };

  return (
    <div ref={itemRef} className="space-y-2">
      <h4 className="font-semibold text-sm mt-3">Wyszukaj działkę</h4>

      {/* Search by click on map */}
      <Button variant="outline" className="w-full" disabled={status === "loading"} onClick={findParcelByXY}>
        {status === "loading" ? <><Loader /> Wyszukuję...</> : "Pobierz działkę z klikniętego punktu"}
      </Button>

      <div className="border-t pt-2 mt-2">
        <p className="text-xs text-gray-500 mb-2">lub wyszukaj ręcznie:</p>
      </div>

      <Select onValueChange={fetchDistricts} defaultValue={voivodeship} disabled>
        <SelectTrigger>
          <SelectValue placeholder="Województwo" />
        </SelectTrigger>
        <SelectContent style={{ maxWidth: itemRef.current?.clientWidth }}>
          {voivodeships?.map(({ value, label }) => (
            <SelectItem className="max-w-full" key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select onValueChange={fetchTenants} defaultValue={district} disabled>
        <SelectTrigger>
          <SelectValue placeholder="Powiat" />
        </SelectTrigger>
        <SelectContent style={{ maxWidth: itemRef.current?.clientWidth }}>
          {districts?.map(({ value, label }) => (
            <SelectItem className="max-w-full" key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select onValueChange={fetchPrecincts} defaultValue={tenant} disabled>
        <SelectTrigger>
          <SelectValue placeholder="Gmina" />
        </SelectTrigger>
        <SelectContent style={{ maxWidth: itemRef.current?.clientWidth }}>
          {tenants?.map(({ value, label }) => (
            <SelectItem className="max-w-full" key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select onValueChange={setSelectedPrecinct} defaultValue={precincts[0]?.value}>
        <SelectTrigger>
          <SelectValue placeholder="Obręb" />
        </SelectTrigger>
        <SelectContent style={{ maxWidth: itemRef.current?.clientWidth }}>
          {precincts?.map(({ value, label }) => (
            <SelectItem className="max-w-full" key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        placeholder="Nr działki (np. 552 lub 552/1)"
        type="text"
        value={parcelNumber}
        onChange={(event) => setParcelNumber(event.target.value)}
      />
      <Button className="w-full" disabled={status === "loading" || !parcelNumber} onClick={findParcel}>
        {status === "loading" ? <><Loader /> Wyszukuję</> : "Wyszukaj działkę"}
      </Button>
      {selectedPrecinct && (
        <p className="text-xs text-gray-400">Obręb: {selectedPrecinct}</p>
      )}
    </div>
  );
}

export default UldkSearch;
